import { call, get } from 'vuex-pathify';

import Feature from 'ol/Feature.js';
import { GeoJSON, MVT } from 'ol/format';
import { Tile as TileLayer, Vector as VectorLayer, VectorTile as VectorTileLayer } from 'ol/layer';
import { Vector as VectorSource, TileWMS, XYZ, WMTS, VectorTile as VectorTileSource, Cluster } from 'ol/source';
import WMTSTileGrid from 'ol/tilegrid/WMTS';
import { Point } from 'ol/geom';

export default {
  computed: {
    dataSources: get('featuresData/dataSources'),
    dataSourcesFeatures: get('featuresData/dataSourcesFeatures'),
    layers: get('featuresData/layers'),
    projects: get('featuresData/projects'),
    settingsValues: get('admin/settings'),
    offersLayersIds() {
      return this.$_config.languages.reduce((total, current) => {
        total[current] = ['brownfields', 'greenfields', 'invested'].reduce((tot, cur) => {
          tot[cur] = this.settingsValues[`${cur.toLocaleUpperCase()}_LAYERS_SETTING`]?.value?.layers?.[current];
          return tot;
        }, {});
        return total;
      }, {});
    },
    offersCartogramLayersIds() {
      return ['voivodeship', 'district', 'commune'].reduce((total, current) => {
        total[current] = this.settingsValues[`${current.toLocaleUpperCase()}_SETTING`]?.value?.layer;
        return total;
      }, {});
    },
    offersCartogramDataSourcesNames() {
      return Object.keys(this.offersCartogramLayersIds).reduce((total, current) => {
        const layerId = this.offersCartogramLayersIds[current];
        const layer = this.layers[layerId];
        total[current] = layer.dataSourceName;
        return total;
      }, {});
    },
    offersCartogramLayersMinMaxZooms() {
      return {
        voivodeship: {
          minZoom: 1,
          maxZoom: 7,
        },
        district: {
          minZoom: 7,
          maxZoom: 10,
        },
        commune: {
          minZoom: 10,
          maxZoom: 13,
        },
        offers: {
          minZoom: 13,
          maxZoom: 28,
        },
      };
    },
    currentLng() {
      return this.$i18n.locale;
    },
    project() {
      return this.projects[this.currentLng];
    },
    projectLayersGeojsons: get('featuresData/projectLayersGeojsons'),

    // Mobile
    mobile: get('tools/mobile'),
    currentOffers: get('featuresData/currentOffers'),
  },
  methods: {
    getDataSourceFeature: call('featuresData/getDataSourceFeature'),
    addProjectLayer(layer, index) {
      if (layer.type === 'service_layer') {
        this.addProjectServiceLayer(layer, index);
      } else if (layer.supports_mvt) {
        this.addProjectMvtLayer(layer, index);
      }
    },

    // Mvt layers
    addProjectMvtLayer(layer, index) {
      const mvtLayer = this.getProjectMvtLayer(layer, index);
      this.getLayerBy('projectLayers', { by: 'name' }).getLayers().push(mvtLayer);
    },
    getProjectMvtLayer(
      { visible, style, labels_visible, opacity, id, name, geometry_type, data_source_name: dataSourceName } = {},
      index
    ) {
      const isOffers = Object.values(this.offersLayersIds[this.currentLng]).includes(id);
      const minMaxZooms = {
        minZoom: 1,
        maxZoom: 28,
      };
      if (this.mobile) {
        minMaxZooms.minZoom = 13;
      } else if (isOffers) {
        minMaxZooms.minZoom = this.offersCartogramLayersMinMaxZooms['offers'].minZoom;
        minMaxZooms.maxZoom = this.offersCartogramLayersMinMaxZooms['offers'].maxZoom;
      } else {
        minMaxZooms.minZoom = this.getMinzoomFromStyle(style);
        minMaxZooms.maxZoom = this.getMaxzoomFromStyle(style);
      }
      const styleAttributes = undefined;
      return new VectorTileLayer({
        className: `ol-layer ol-layer-mvt-${id}`,
        opacity,
        id,
        ...(isOffers ? { type: 'offer' } : {}),
        dataSourceName,
        name,
        visible,
        ...minMaxZooms,
        source: new VectorTileSource({
          cacheSize: 16,
          format: new MVT(),
          url: `${process.env.VUE_APP_API_URL}/layers/features_layers/${id}/mvt/{z}/{x}/{y}`,
          projection: this.$_config.mapDataCoordinateSystem.epsg,
          tileLoadFunction: tile => {
            this.defaultMvtSourceLoader(tile, id, { styleAttributes });
          },
          minZoom: 8,
          maxZoom: 16,
        }),
        style: f => this.getFeatureStyle(f, style, geometry_type, labels_visible, id, isOffers && !this.mobile),
        ...(index && { zIndex: index }),
        renderMode: 'image',
      });
    },

    // Offers cartogram geojson layers
    addOffersCartogramLayer(type) {
      const geojson = this.projectLayersGeojsons[this.offersCartogramLayersIds[type]];
      const offersCartogramLayer = this.getOffersCartogramLayer(type, geojson, this.offersCartogramLayersIds[type]);
      this.getLayerBy('offersCartogramLayers', { by: 'name' }).getLayers().push(offersCartogramLayer);
    },
    getOffersCartogramLayer(type, geojson, id) {
      const ids = this.offersTreeFilterIds[type];
      if (ids?.length) {
        geojson = { ...geojson, features: geojson.features.filter(feature => ids.includes(feature.id)) };
      }
      return new VectorLayer({
        className: `ol-layer ol-layer-vector-${type}`,
        id,
        type,
        ...this.offersCartogramLayersMinMaxZooms[type],
        source: new VectorSource({
          format: new GeoJSON(),
          features: new GeoJSON().readFeatures(geojson, {
            dataProjection: geojson.crs.properties.name,
            featureProjection: this.$_config.mapDataCoordinateSystem.epsg,
          }),
        }),
        style: f => this.getOffersCartogramFeatureStyle(f, type),
      });
    },

    // Offers mobile cluster geojson layers
    addOffersClusterLayer() {
      const offersClusterLayer = this.getOffersClusterLayer();
      this.map.addLayer(offersClusterLayer);
    },
    getOffersClusterLayer() {
      const loader = source => {
        const geojson = {
          crs: {
            properties: { name: this.$_config.mapDataCoordinateSystem?.epsg },
            type: 'name',
          },
          features: this.currentOffers,
          type: 'FeatureCollection',
        };
        source.addFeatures(
          source.getFormat().readFeatures(geojson, {
            dataProjection: geojson.crs.properties.name,
            featureProjection: this.$_config.mapDataCoordinateSystem.epsg,
          })
        );
      };
      const source = new Cluster({
        source: new VectorSource({
          format: new GeoJSON(),
          loader: function () {
            loader(this);
          },
        }),
        createCluster: function (point, features) {
          let ft = new Feature({
            geometry: point,
            features: features,
            type: 'cluster',
          });
          ft.setId(`cluster-${features.map(ft => ft.getId() || ft.get('id')).join('-')}`);
          return ft;
        },
        geometryFunction: feature => {
          return new Point(this.$_getGeometryCentroid(feature.getGeometry()));
        },
      });
      const style = {
        'fill-color': this.$_colors.secondary,
        'fill-opacity': 1,
        'fill-outline-color': this.$_colors.secondary,
        'fill-outline-opacity': 1,
        'line-width': 1,
        'line-dash': [],
      };
      return new VectorLayer({
        className: `ol-layer ol-layer-vector-offers-cluster`,
        opacity: 1,
        id: 'offers-cluster',
        // dataSourceName,
        // name,
        visible: true,
        // minZoom: this.getMinzoomFromStyle(style),
        maxZoom: 13,
        source,
        style: f => this.getFeatureStyle(f, style, 'geometry_type', 'labels_visible', 'id', false, true),
        zIndex: 999,
      });
    },
    async updateOffersClusterLayerFeatures() {
      this.getLayerBy('offers-cluster')?.getSource()?.getSource()?.refresh();
    },

    // Service layers
    addProjectServiceLayer(layer, index) {
      const serviceLayer = this.getServiceLayer(layer, index);
      this.getLayerBy('projectLayers', { by: 'name' }).getLayers().push(serviceLayer);
    },
    getServiceLayer(
      { name, id, url, visible, opacity, style, parameters, service_type, service_layers_names, srid } = {},
      index
    ) {
      const styleMinZoom = style?.minzoom;
      const styleMaxZoom = style?.maxzoom;
      if (service_type === 'wmts') {
        const { format, version, CRS } = parameters;
        return new TileLayer({
          className: `ol-layer ol-layer-wmts-${id}`,
          name,
          id,
          visible,
          opacity,
          ...(styleMaxZoom && { maxZoom: styleMaxZoom }),
          ...(styleMinZoom && { minZoom: styleMinZoom }),
          source: new WMTS({
            url,
            format,
            version,
            crossOrigin: '',
            projection: CRS,
            matrixSet: CRS,
            tileGrid: new WMTSTileGrid(parameters.options),
            wrapX: true,
            style: '',
            layer: Array.isArray(service_layers_names) ? service_layers_names.join() : service_layers_names,
          }),
          ...(index && { zIndex: index }),
        });
      } else if (service_type === 'wms') {
        return new TileLayer({
          className: `ol-layer ol-layer-wms-${id}`,
          name,
          id,
          visible,
          opacity,
          ...(styleMaxZoom && { maxZoom: styleMaxZoom }),
          ...(styleMinZoom && { minZoom: styleMinZoom }),
          source: new TileWMS({
            url,
            params: {
              LAYERS: Array.isArray(service_layers_names) ? service_layers_names.join() : service_layers_names,
              TILED: true,
              SRS: parameters?.CRS || this.$_config.mapDataCoordinateSystem?.epsg,
              CRS: parameters?.CRS || this.$_config.mapDataCoordinateSystem?.epsg,
              VERSION: parameters.version,
            },
            projection: parameters?.CRS || this.$_config.mapDataCoordinateSystem?.epsg,
          }),
          ...(index && { zIndex: index }),
        });
      } else if (service_type === 'xyz') {
        const { minZoom: parametrMinZoom, maxZoom: parametrMaxZoom } = parameters;
        return new TileLayer({
          className: `ol-layer ol-layer-xyz-${id}`,
          name,
          id,
          visible,
          opacity,
          ...(styleMaxZoom && { maxZoom: styleMaxZoom }),
          ...(styleMinZoom && { minZoom: styleMinZoom }),
          source: new XYZ({
            url,
            ...(parametrMaxZoom && { maxZoom: parametrMaxZoom }),
            ...(parametrMinZoom && { minZoom: parametrMinZoom }),
            params: {
              LAYERS: Array.isArray(service_layers_names) ? service_layers_names.join() : service_layers_names,
              TILED: true,
              SRS: `EPSG:${srid}`,
            },
          }),
          ...(index && { zIndex: index }),
        });
      }
    },

    getMaxzoomFromStyle(style) {
      const styleMaxZooms = [style.maxzoom || 28];
      if (style.uniques || style.ranges) {
        const styles = Object.values((style.uniques || style.ranges).values);
        for (const singleStyle of styles) {
          styleMaxZooms.push(singleStyle.maxzoom || 28);
        }
      }
      return Math.max(...styleMaxZooms);
    },
    getMinzoomFromStyle(style) {
      let minzoomsFromStyles = [style.minzoom];
      const isSingleStyle = style.uniques === undefined && style.ranges === undefined ? true : false;
      if (!isSingleStyle) {
        const styleMode =
          style.uniques !== undefined ? Object.values(style.uniques.values) : Object.values(style.ranges.values);
        for (let oneStyle of styleMode) {
          minzoomsFromStyles.push(oneStyle.minzoom);
        }
      }
      return Math.min(...minzoomsFromStyles) - 1;
    },

    toggleProjectLayer({ id, value = false } = {}) {
      const layer = this.getLayerBy(id, { group: 'projectLayers' });
      layer?.setVisible(value);
      this.map.updateSize();
    },
    toggleProject(project = this.project) {
      const projectMapLayers = this.getLayerBy('projectLayers', { by: 'name' });
      projectMapLayers.getLayers().clear();
      if (!project) return;
      const flatProjectLayers = this.$_getFlatGroupsLayers(project.layers).layers;
      flatProjectLayers.reverse().forEach((layer, index) => this.addProjectLayer(layer, index));
      this.addOffersClusterLayer();
    },

    toggleOffersCartogram() {
      Object.keys(this.offersCartogramLayersIds).forEach(type => {
        this.addOffersCartogramLayer(type);
      });
    },

    async zoomToProjectLayerFeature({ lid, fid, group = 'projectLayers' } = {}) {
      const layer = this.getLayerBy(lid, { group });
      if (layer instanceof VectorLayer) {
        let featureOl = layer
          .getSource()
          .getFeatures()
          ?.find(feature => feature.getId() === fid);
        if (featureOl) {
          const featureGeometry = featureOl.getGeometry();
          if (featureGeometry instanceof Point) {
            this.setView({ center: featureGeometry.getCoordinates(), zoom: 17 });
            return;
          } else {
            this.fitView({ value: featureGeometry });
            return;
          }
        }
      }
      const dataSourceName = this.layers[lid]?.dataSourceName;
      if (dataSourceName) {
        let feature = this.dataSourcesFeatures[dataSourceName]?.[fid];
        if (!feature) await this.getDataSourceFeature({ dataSourceName, featureId: fid });
        feature = this.dataSourcesFeatures[dataSourceName][fid];
        const featureOl = new GeoJSON().readFeature(feature, {
          featureProjection: this.$_config.mapDataCoordinateSystem.epsg,
        });
        const featureGeometry = featureOl.getGeometry();
        if (featureGeometry instanceof Point) {
          this.setView({ center: featureGeometry.getCoordinates(), zoom: 17 });
          return;
        } else {
          this.fitView({ value: featureGeometry });
          return;
        }
      }
    },
  },
  watch: {
    project: {
      handler(nV) {
        this.toggleProject(nV);
      },
      deep: true,
      immediate: false,
    },
  },
  mounted() {
    this.$root.$off('toggleProjectLayerVisibility');
    this.$root.$on('toggleProjectLayerVisibility', this.toggleProjectLayer);
    this.$root.$off('updateOffersClusterLayerFeatures');
    this.$root.$on('updateOffersClusterLayerFeatures', this.updateOffersClusterLayerFeatures);
    this.$root.$off('zoomToProjectLayerFeature');
    this.$root.$on('zoomToProjectLayerFeature', this.zoomToProjectLayerFeature);
  },
};
